import React, { useRef, useState } from 'react'
import { Button, Menu, MenuItem, Radio } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import 'src/assets/css/components/pipelineFilters.css'
import IPipeline, { latestRun, PipelineHealthStatus, PipelineType } from 'src/models/IPipeline'
import { JobStatus } from 'src/models/IJob'
import { statusLabel } from 'src/utils/jobStatus'
import { pipelineType, pipelineTypeLabel } from 'src/utils/pipelineType'

/**
 * Filter bar for the pipelines list (MEL-657).
 *
 * Type leads (MEL-702): ingestion vs workflow is the first split of the list. Health and Last Run
 * Status follow, because stability is what MEL-657 is for; Trigger and Plugin come last as the
 * "find my pipeline" filters that only start to pay off at 100+ pipelines.
 *
 * Single-select with an "All" reset, as the design draws it. That also means the active selection
 * shows in the dropdown's own label, so no separate chip or count badge is needed.
 */

/**
 * How a pipeline gets started.
 *
 * The design draws only Manual and Scheduled, but a pipeline chained to an upstream pipeline or to
 * the workspace Deploy is neither: calling that "Manual" would tell the user it needs a human,
 * which is the opposite of true. It gets its own value, and since options are derived from the
 * loaded pipelines it is simply never offered in a workspace that has none.
 */
export enum PipelineTrigger {
    Scheduled = 'SCHEDULED',
    Triggered = 'TRIGGERED',
    Manual = 'MANUAL',
}

const TRIGGER_LABELS: Record<PipelineTrigger, string> = {
    [PipelineTrigger.Scheduled]: 'Scheduled',
    [PipelineTrigger.Triggered]: 'Triggered by another pipeline',
    [PipelineTrigger.Manual]: 'Manual',
}

/** A schedule wins over an upstream trigger — it runs on the clock whatever else also starts it. */
export const pipelineTrigger = (pipeline: IPipeline): PipelineTrigger =>
    pipeline.schedule
        ? PipelineTrigger.Scheduled
        : pipeline.triggeredBy?.length
            ? PipelineTrigger.Triggered
            : PipelineTrigger.Manual

/**
 * The plugins a pipeline runs on, as plugin names (`tap-google-analytics`, `dbt-postgres`).
 *
 * Taken from the plugin FQN (`extractors/tap-google-analytics--matatika`) on the pipeline's
 * embedded data components, **not** from the component's own name: a name is what this workspace
 * called one connection, whereas the ticket asks to filter by plugin, and two components can share
 * a plugin under different names. The variant is dropped — nobody thinks of `--matatika` as part
 * of which connector they are running.
 */
export const pipelinePlugins = (pipeline: IPipeline): string[] =>
    Array.from(new Set(
        (pipeline._embedded?.dataComponents || [])
            .map(dc => dc.dataPlugin)
            .filter((fqn): fqn is string => !!fqn)
            .map(fqn => fqn.substring(fqn.indexOf('/') + 1).split('--')[0])))

/** `undefined` = "All", i.e. this filter is not applied. */
export interface IPipelineFilterState {
    type?: PipelineType
    health?: PipelineHealthStatus
    lastRunStatus?: JobStatus
    trigger?: PipelineTrigger
    plugin?: string
}

export const noPipelineFilters: IPipelineFilterState = {}

export const hasActiveFilter = (filters: IPipelineFilterState): boolean =>
    !!filters.type || !!filters.health || !!filters.lastRunStatus || !!filters.trigger || !!filters.plugin

/**
 * Whether a pipeline passes the current filters. AND across filters, and exported so the view can
 * hand it straight to `Search` — the list and the filter bar then cannot disagree.
 */
export const matchesPipelineFilters = (pipeline: IPipeline, filters: IPipelineFilterState): boolean => {
    if (filters.type && pipelineType(pipeline) !== filters.type)
        return false

    if (filters.health && pipeline.health?.status !== filters.health)
        return false

    if (filters.lastRunStatus && latestRun(pipeline)?.status !== filters.lastRunStatus)
        return false

    if (filters.trigger && pipelineTrigger(pipeline) !== filters.trigger)
        return false

    // a pipeline runs several plugins, so this asks "uses it", not "is it"
    if (filters.plugin && !pipelinePlugins(pipeline).includes(filters.plugin))
        return false

    return true
}

interface IFilterOption<V> {
    value: V
    label: string
}

interface IFilterDropdownProps<V> {
    name: string
    /** Shown when nothing is selected; replaced by the selected option's label. */
    placeholder: string
    options: IFilterOption<V>[]
    selected?: V
    onSelect: (value?: V) => void
}

const FilterDropdown = <V extends string>(props: IFilterDropdownProps<V>): JSX.Element => {
    const [isOpen, setOpen] = useState(false)
    const anchorEl = useRef<EventTarget & HTMLButtonElement>()

    const selectedOption = props.options.find(o => o.value === props.selected)

    const handleSelect = (value?: V) => {
        props.onSelect(value)
        setOpen(false)
    }

    return <>
        <Button
            className={`pipeline-filter-button${selectedOption ? ' active' : ''}`}
            aria-label={props.name}
            title={props.name}
            disableElevation
            endIcon={<ExpandMoreIcon />}
            onClick={e => { anchorEl.current = e.currentTarget; setOpen(true) }}>
            {selectedOption?.label || props.placeholder}
        </Button>
        <Menu
            // match the pipeline card's dropdowns: keep the page scrollable, and close on scroll
            // rather than float away from the anchor (MUI v4 positions a popover once, at open)
            disableScrollLock
            open={isOpen}
            anchorEl={anchorEl.current}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            getContentAnchorEl={null}
            onClose={() => setOpen(false)}>
            <MenuItem dense onClick={() => handleSelect(undefined)}>
                <Radio size='small' color='primary' checked={!props.selected} />
                All
            </MenuItem>
            {props.options.map(option =>
                <MenuItem
                    key={option.value}
                    dense
                    onClick={() => handleSelect(option.value)}>
                    <Radio size='small' color='primary' checked={props.selected === option.value} />
                    {option.label}
                </MenuItem>)}
        </Menu>
    </>
}

interface IPipelineFiltersProps {
    /** The loaded pipelines — option lists are derived from these, never hardcoded. */
    pipelines: IPipeline[] | undefined
    filters: IPipelineFilterState
    onChange: (filters: IPipelineFilterState) => void
}

const PipelineFilters = (props: IPipelineFiltersProps): JSX.Element => {
    const pipelines = props.pipelines || []

    /*
     * Options come from the pipelines actually loaded, so a workspace is never offered a filter
     * that would return nothing. Health is the exception: Unstable stays offered even when nothing
     * is currently unstable, because its absence is the answer the user came for — offering it and
     * returning the empty state says "none", whereas hiding it says nothing at all.
     */
    const healthOptions = [
        { value: PipelineHealthStatus.Unstable, label: 'Unstable' },
        { value: PipelineHealthStatus.Stable, label: 'Stable' },
    ]

    // in a fixed order, and only the types present - like Trigger, offered even when every pipeline
    // is one type, which is every workspace until its first ingestion
    const presentTypes = new Set(pipelines.map(pipelineType))
    const typeOptions = [PipelineType.Ingestion, PipelineType.Workflow]
        .filter(type => presentTypes.has(type))
        .map(type => ({ value: type, label: pipelineTypeLabel(type) }))

    const presentRunStatuses = Array.from(new Set(
        pipelines
            .map(p => latestRun(p)?.status)
            .filter((s): s is JobStatus => !!s)))

    // title case like the other options, although the run chips keep the upper case of the label
    const lastRunStatusOptions = presentRunStatuses.map(status => {
        const label = statusLabel(status)
        return { value: status, label: label.charAt(0) + label.slice(1).toLowerCase() }
    })

    // listed in the fixed order above rather than the order pipelines happen to arrive in
    const presentTriggers = new Set(pipelines.map(pipelineTrigger))
    const triggerOptions = (Object.keys(TRIGGER_LABELS) as PipelineTrigger[])
        .filter(trigger => presentTriggers.has(trigger))
        .map(trigger => ({ value: trigger, label: TRIGGER_LABELS[trigger] }))

    // no flatMap: nothing else in this app relies on it, and the toolchain here is CRA 4 / ES5
    const pluginOptions = Array.from(new Set(
        pipelines.reduce<string[]>((all, p) => all.concat(pipelinePlugins(p)), [])))
        .sort()
        .map(plugin => ({ value: plugin, label: plugin }))

    return <div className='pipeline-filters'>
        {/*
          * Names the row before the controls: on its own, a row of dropdowns reading "Health",
          * "Last Run Status", "Trigger" doesn't say it is a filter bar — and once one is applied
          * its label is replaced by the selected value, losing even that hint.
          */}
        <span className='pipeline-filters-label'>Filter</span>
        {!!typeOptions.length &&
            <FilterDropdown
                name='Type'
                placeholder='Type'
                options={typeOptions}
                selected={props.filters.type}
                onSelect={type => props.onChange({ ...props.filters, type })} />}
        <FilterDropdown
            name='Health'
            placeholder='Health'
            options={healthOptions}
            selected={props.filters.health}
            onSelect={health => props.onChange({ ...props.filters, health })} />
        {!!lastRunStatusOptions.length &&
            <FilterDropdown
                name='Last Run Status'
                placeholder='Last Run Status'
                options={lastRunStatusOptions}
                selected={props.filters.lastRunStatus}
                onSelect={lastRunStatus => props.onChange({ ...props.filters, lastRunStatus })} />}
        {/*
          * Trigger and Plugin are the "find my pipeline quickly" half of the ticket, not the
          * stability half — they come after it, and each is dropped entirely when the loaded
          * pipelines offer nothing to choose between.
          */}
        {/*
          * Offered whenever any pipeline is loaded, on the same `length > 0` test as the others.
          * It used to hide itself when every pipeline started the same way — one option then
          * selects the whole list, so it narrows nothing. But a filter bar missing the filter the
          * design draws reads as a bug, not as a hint that everything is scheduled, and that is
          * exactly how it was reported (QA round 2). Answering "how do my pipelines start?" with
          * a single option is worth more than the row it saves.
          */}
        {!!triggerOptions.length &&
            <FilterDropdown
                name='Trigger'
                placeholder='Trigger'
                options={triggerOptions}
                selected={props.filters.trigger}
                onSelect={trigger => props.onChange({ ...props.filters, trigger })} />}
        {/* a lone plugin option still narrows: it drops pipelines that carry no components */}
        {!!pluginOptions.length &&
            <FilterDropdown
                name='Plugin'
                placeholder='Plugin'
                options={pluginOptions}
                selected={props.filters.plugin}
                onSelect={plugin => props.onChange({ ...props.filters, plugin })} />}
        {hasActiveFilter(props.filters) &&
            <Button
                className='pipeline-filter-clear'
                disableElevation
                onClick={() => props.onChange(noPipelineFilters)}>
                Clear
            </Button>}
    </div>
}

export default PipelineFilters
